import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Chip,
  IconButton,
  Box,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonChecked as RadioIcon,
  TextFields as TextIcon,
  CalendarMonth as DateIcon,
  AttachFile as FileIcon,
  Numbers as NumberIcon,
  List as ListIcon,
  CheckBox as CheckBoxIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { QuestionDto, AnswerType } from '../../types/questions';

interface QuestionCardProps {
  question: QuestionDto;
  onEdit: (question: QuestionDto) => void;
  onDelete: (questionId: number) => void;
  isDeleting?: boolean;
}

const answerTypeIcons: { [key: string]: React.ReactElement } = {
  [AnswerType.BOOLEAN]: <CheckCircleIcon fontSize="small" />,
  [AnswerType.TEXT]: <TextIcon fontSize="small" />,
  [AnswerType.TEXT_AREA]: <ArticleIcon fontSize="small" />,
  [AnswerType.NUMBER]: <NumberIcon fontSize="small" />,
  [AnswerType.DATE]: <DateIcon fontSize="small" />,
  [AnswerType.RADIO]: <RadioIcon fontSize="small" />,
  [AnswerType.MULTISELECT]: <CheckBoxIcon fontSize="small" />,
  [AnswerType.FILE]: <FileIcon fontSize="small" />,
  [AnswerType.LIST_OBJECT]: <ListIcon fontSize="small" />,
};

const answerTypeColors: { [key: string]: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' } = {
  [AnswerType.BOOLEAN]: 'success',
  [AnswerType.TEXT]: 'primary',
  [AnswerType.TEXT_AREA]: 'primary',
  [AnswerType.NUMBER]: 'info',
  [AnswerType.DATE]: 'info',
  [AnswerType.RADIO]: 'secondary',
  [AnswerType.MULTISELECT]: 'secondary',
  [AnswerType.FILE]: 'warning',
  [AnswerType.LIST_OBJECT]: 'default',
};

const QuestionCard: React.FC<QuestionCardProps> = ({ question, onEdit, onDelete, isDeleting = false }) => {
  const theme = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id || 0 });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        position: 'relative',
        overflow: 'visible',
        cursor: isDragging ? 'grabbing' : 'grab',
        '&:hover': {
          boxShadow: theme.shadows[4],
          '& .action-buttons': {
            opacity: 1,
          },
        },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            {...attributes}
            {...listeners}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'grab',
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
              },
            }}
          >
            <DragIndicatorIcon />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'bold',
                    color: 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  #{question.questionNumber}
                </Typography>
                <Typography variant="subtitle1" fontWeight="medium">
                  {question.questionShorten}
                </Typography>
                <Chip
                  icon={answerTypeIcons[question.answerType]}
                  label={question.answerType}
                  size="small"
                  color={answerTypeColors[question.answerType]}
                  variant="outlined"
                />
                {question.isRequiredAnswer && (
                  <Chip label="Required" size="small" color="error" variant="filled" />
                )}
                {question.requiresVerification && (
                  <Chip label="Verification" size="small" color="warning" variant="outlined" />
                )}
              </Stack>

              <Typography variant="body2" color="text.secondary">
                {question.question}
              </Typography>

              {question.options && question.options.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                  {question.options.map((option) => (
                    <Chip
                      key={option.key}
                      label={option.label}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  ))}
                </Stack>
              )}

              <Stack direction="row" spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Key: {question.questionKey}
                </Typography>
                {question.questionDataKey && (
                  <>
                    <Typography variant="caption" color="text.secondary">•</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Maps to: {question.questionDataKey}
                    </Typography>
                  </>
                )}
              </Stack>
            </Stack>
          </Box>

          <Stack
            className="action-buttons"
            direction="row"
            spacing={1}
            sx={{
              opacity: 0.5,
              transition: 'opacity 0.2s',
            }}
          >
            <Tooltip title="Edit question">
              <IconButton
                size="small"
                onClick={() => onEdit(question)}
                sx={{
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isDeleting ? "Archiving..." : "Archive question"}>
              <span>
                <IconButton
                  size="small"
                  disabled={isDeleting}
                  onClick={() => {
                    if (!isDeleting && confirm('Are you sure you want to archive this question? It will no longer appear in new sessions.')) {
                      onDelete(question.id!);
                    }
                  }}
                  sx={{
                    '&:hover': {
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      color: 'warning.main',
                    },
                  }}
                >
                  {isDeleting ? (
                    <CircularProgress size={18} color="warning" />
                  ) : (
                    <DeleteIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default QuestionCard;